interface SpeechRecognitionEvent {
  resultIndex: number;
  results: SpeechRecognitionResultList;
}

interface SpeechRecognitionErrorEvent {
  error: string;
  message?: string;
}

interface SpeechRecognitionCallbacks {
  onStart?: () => void;
  onResult?: (transcript: string, isFinal: boolean) => void;
  onEnd?: () => void;
  onError?: (error: string) => void;
}

interface SpeechRecognitionOptions {
  continuous?: boolean;
  interimResults?: boolean;
  lang?: string;
  silenceTimeout?: number; // in milliseconds
}

type EventCallback = (...args: any[]) => void;

class SpeechRecognitionService {
  private recognition: any = null;
  private _isSupported = false;
  private _isListening = false;
  private eventListeners: Map<string, Set<EventCallback>> = new Map();
  private silenceTimeout: NodeJS.Timeout | null = null;
  private options: SpeechRecognitionOptions;

  constructor(options: SpeechRecognitionOptions = {}) {
    this.options = {
      continuous: true,
      interimResults: true,
      lang: 'en-US',
      silenceTimeout: 4000, // 4 seconds
      ...options
    };

    this.initialize();
  }

  private initialize() {
    // Check if Speech Recognition is supported
    const SpeechRecognition =
      (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;

    if (SpeechRecognition) {
      this._isSupported = true;
      this.recognition = new SpeechRecognition();

      // Configure recognition
      this.recognition.continuous = this.options.continuous;
      this.recognition.interimResults = this.options.interimResults;
      this.recognition.lang = this.options.lang;

      // Set up event handlers
      this.recognition.onstart = this.handleStart.bind(this);
      this.recognition.onresult = this.handleResult.bind(this);
      this.recognition.onend = this.handleEnd.bind(this);
      this.recognition.onerror = this.handleError.bind(this);

      console.log('Speech recognition initialized with options:', this.options);
    } else {
      this._isSupported = false;
      console.log('Speech recognition not supported in this browser');
    }
  }

  private resetSilenceTimeout() {
    if (this.silenceTimeout) {
      clearTimeout(this.silenceTimeout);
    }

    if (this.options.silenceTimeout && this.options.silenceTimeout > 0) {
      this.silenceTimeout = setTimeout(() => {
        console.log(`No speech detected for ${this.options.silenceTimeout}ms, stopping recognition`);
        this.stop();
      }, this.options.silenceTimeout);
    }
  }

  // Event Emitter API
  on(event: string, callback: EventCallback): () => void {
    if (!this.eventListeners.has(event)) {
      this.eventListeners.set(event, new Set());
    }
    this.eventListeners.get(event)!.add(callback);
    
    // Return unsubscribe function
    return () => {
      this.eventListeners.get(event)?.delete(callback);
    };
  }

  off(event: string, callback: EventCallback): void {
    this.eventListeners.get(event)?.delete(callback);
  }

  private emit(event: string, ...args: any[]): void {
    this.eventListeners.get(event)?.forEach(callback => {
      try {
        callback(...args);
      } catch (e) {
        console.error(`Error in speech recognition event handler for ${event}:`, e);
      }
    });
  }

  private handleStart() {
    this._isListening = true;
    console.log('Speech recognition started');
    this.resetSilenceTimeout();
    this.emit('start');
  }

  private handleResult(event: SpeechRecognitionEvent) {
    this.resetSilenceTimeout();

    let finalTranscript = '';
    let interimTranscript = '';

    for (let i = event.resultIndex; i < event.results.length; i++) {
      const transcript = event.results[i][0].transcript;
      if (event.results[i].isFinal) {
        finalTranscript += transcript;
      } else {
        interimTranscript += transcript;
      }
    }

    if (finalTranscript) {
      this.emit('result', finalTranscript, true);
    }
    if (interimTranscript) {
      this.emit('result', interimTranscript, false);
    }
  }

  private handleEnd() {
    this._isListening = false;
    if (this.silenceTimeout) {
      clearTimeout(this.silenceTimeout);
      this.silenceTimeout = null;
    }
    console.log('Speech recognition ended');
    this.emit('end');
  }

  private handleError(event: SpeechRecognitionErrorEvent) {
    this._isListening = false;
    if (this.silenceTimeout) {
      clearTimeout(this.silenceTimeout);
      this.silenceTimeout = null;
    }

    console.error('Speech recognition error:', event.error, event.message);
    this.emit('error', event.error);
  }

  // Public API methods
  setCallbacks(callbacks: SpeechRecognitionCallbacks) {
    if (callbacks.onStart) this.on('start', callbacks.onStart);
    if (callbacks.onResult) this.on('result', callbacks.onResult);
    if (callbacks.onEnd) this.on('end', callbacks.onEnd);
    if (callbacks.onError) this.on('error', callbacks.onError);
  }

  isSupported(): boolean {
    return this._isSupported;
  }

  isListening(): boolean {
    return this._isListening;
  }

  async start(): Promise<boolean> {
    if (!this._isSupported || !this.recognition) {
      console.error('Speech recognition not supported');
      return false;
    }

    if (this._isListening) {
      console.log('Speech recognition already running');
      return true;
    }

    try {
      this.recognition.start();
      return true;
    } catch (error: any) {
      console.error('Failed to start speech recognition:', error);
      this.emit('error', error.message || 'Failed to start');
      return false;
    }
  }

  stop() {
    if (this.recognition && this._isListening) {
      this.recognition.stop();
    }
  }

  abort() {
    if (this.recognition) {
      this.recognition.abort();
    }
  }

  // Update options (reinitializes the recognition)
  updateOptions(newOptions: Partial<SpeechRecognitionOptions>) {
    this.options = { ...this.options, ...newOptions };

    if (this.recognition) {
      // Stop current recognition if running
      if (this._isListening) {
        this.stop();
      }

      // Reinitialize with new options
      this.initialize();
    }
  }

  // Get current options
  getOptions(): SpeechRecognitionOptions {
    return { ...this.options };
  }

  // Cleanup
  destroy() {
    this.stop();
    if (this.silenceTimeout) {
      clearTimeout(this.silenceTimeout);
    }
    this.eventListeners.clear();
  }
}

// Create lazy initialized singleton instance
let speechRecognitionServiceInstance: SpeechRecognitionService;

export function getSpeechRecognitionService(): SpeechRecognitionService {
  if (!speechRecognitionServiceInstance) {
    speechRecognitionServiceInstance = new SpeechRecognitionService();
  }
  return speechRecognitionServiceInstance;
}

// Backwards compatibility export for existing imports
export const speechRecognitionService = new Proxy({}, {
  get: function(target, prop) {
    const service = getSpeechRecognitionService();
    const value = (service as any)[prop];
    if (typeof value === 'function') {
      return value.bind(service);
    }
    return value;
  }
}) as SpeechRecognitionService;

export { SpeechRecognitionService };
export type { SpeechRecognitionCallbacks, SpeechRecognitionOptions };